import { useState, useRef, useEffect } from 'react';
import { 
  Mic, MicOff, FileText, Brain, Download, Percent, Euro, Loader2, 
  CheckCircle, RefreshCw, Trash2, Plus, Pencil, Upload,
  ArrowRight, Sparkles, Calculator, MessageSquare, Volume2, X, Camera,
  MapPin, User, Building, Image as ImageIcon
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { parseFR } from '@/lib/utils';
import NumInputFR from '@/components/NumInputFR';

export default function DevisGenerator({ onDevisCreated, prefilledClient }) {
  const [step, setStep] = useState(1);
  const [inputMode, setInputMode] = useState('text');
  const [description, setDescription] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Editable data
  const [editedLines, setEditedLines] = useState([]);
  const [clientInfo, setClientInfo] = useState({
    nom: '',
    adresse: '',
    telephone: '',
    email: '',
    type: 'particulier' // 'particulier' or 'professionnel'
  });
  const [chantierAdresse, setChantierAdresse] = useState('');
  const [sameAsClient, setSameAsClient] = useState(true);
  const [devisInfo, setDevisInfo] = useState({ 
    numero: '', 
    date: new Date().toLocaleDateString('fr-FR'), 
    validite: '30 jours' 
  });
  const [projectTitle, setProjectTitle] = useState('');
  
  // Before photo
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [beforePhotoPreview, setBeforePhotoPreview] = useState(null);
  const beforePhotoRef = useRef(null);
  
  // Discount
  const [discountMode, setDiscountMode] = useState('percent');
  const [discountValue, setDiscountValue] = useState(0);
  const [globalTvaRate, setGlobalTvaRate] = useState(20);
  
  // Voice recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRefs = useRef({});

  useEffect(() => {
    if (prefilledClient) {
      setClientInfo({ ...prefilledClient, type: prefilledClient.type || 'particulier' });
    }
  }, [prefilledClient]);

  // Before photo handling
  const handleBeforePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image');
        return;
      }
      setBeforePhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => setBeforePhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Enregistrement en cours...');
    } catch (error) {
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Enregistrement terminé !');
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;
    
    setTranscribing(true);
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    
    try {
      const response = await api.post('/devis-generator/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDescription(response.data.text);
      toast.success('Transcription terminée !');
    } catch (error) {
      toast.error('Erreur de transcription');
    } finally {
      setTranscribing(false);
    }
  };

  const generateLines = async () => {
    if (!description.trim()) {
      toast.error('Veuillez décrire le chantier');
      return;
    }
    
    setStep(2);
    setGenerating(true);
    
    try {
      const response = await api.post('/devis-generator/generate-lines', {
        description: description,
        project_type: projectTitle || 'Rénovation'
      });
      
      const lines = response.data.lines.map((line, idx) => ({
        id: idx,
        description: line.description,
        quantite: line.quantite || 1,
        unite: line.unite || 'u',
        prix_unitaire: line.prix_unitaire || 0,
        tva_rate: line.tva_rate || 20,
        image: null,
        imagePreview: null
      }));
      
      setEditedLines(lines);
      setProjectTitle(response.data.project_title || projectTitle);
      setStep(3);
      toast.success(`${lines.length} lignes générées !`);
    } catch (error) {
      toast.error('Erreur de génération');
      setStep(1);
    } finally {
      setGenerating(false);
    }
  };

  // Line editing
  const updateLine = (index, field, value) => {
    setEditedLines(prev => prev.map((line, idx) => 
      idx === index ? { ...line, [field]: value } : line
    ));
  };

  const addLine = () => {
    setEditedLines(prev => [...prev, {
      id: Date.now(),
      description: 'Nouvelle prestation',
      quantite: 1,
      unite: 'u',
      prix_unitaire: 0,
      tva_rate: globalTvaRate,
      image: null,
      imagePreview: null
    }]);
  };

  const removeLine = (index) => {
    setEditedLines(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleLineImage = (index, e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        updateLine(index, 'image', file);
        updateLine(index, 'imagePreview', e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Calculations
  const applyDiscount = (price) => {
    if (discountValue <= 0) return price;
    if (discountMode === 'percent') {
      return price * (1 - discountValue / 100);
    } else {
      const totalHT = editedLines.reduce((sum, l) => sum + (l.prix_unitaire * l.quantite), 0);
      if (totalHT <= 0) return price;
      return Math.max(0, price - (discountValue * price / totalHT));
    }
  };

  const calculateLineTotal = (line) => applyDiscount(line.prix_unitaire) * line.quantite;

  const calculateTotals = () => {
    let totalHT = 0, totalTVA = 0;
    editedLines.forEach(line => {
      const lineHT = calculateLineTotal(line);
      totalHT += lineHT;
      totalTVA += lineHT * (line.tva_rate / 100);
    });
    return { ht: totalHT, tva: totalTVA, ttc: totalHT + totalTVA };
  };

  const handleGeneratePDF = async () => {
    setGeneratingPdf(true);
    
    try {
      // Upload before photo if exists
      let beforePhotoUrl = null;
      if (beforePhoto) {
        const photoForm = new FormData();
        photoForm.append('file', beforePhoto);
        try {
          const uploadRes = await api.post('/devis-generator/upload-line-image', photoForm, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          beforePhotoUrl = uploadRes.data.url;
        } catch (e) {
          console.error('Before photo upload failed');
        }
      }
      
      // Upload line images
      const linesWithImages = await Promise.all(editedLines.map(async (line) => {
        let imageUrl = null;
        if (line.image) {
          const formData = new FormData();
          formData.append('file', line.image);
          try {
            const uploadRes = await api.post('/devis-generator/upload-line-image', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            imageUrl = uploadRes.data.url;
          } catch (e) {}
        }
        return {
          description: line.description,
          quantite: line.quantite,
          unite: line.unite,
          prix_unitaire: applyDiscount(line.prix_unitaire),
          tva_rate: line.tva_rate,
          total_ht: calculateLineTotal(line),
          image_url: imageUrl
        };
      }));
      
      const pdfData = {
        client: clientInfo,
        devis_info: {
          ...devisInfo,
          numero: devisInfo.numero || `RR-${Date.now().toString(36).toUpperCase()}`
        },
        project_title: projectTitle,
        chantier_adresse: sameAsClient ? clientInfo.adresse : chantierAdresse,
        before_photo_url: beforePhotoUrl,
        lignes: linesWithImages,
        totaux: calculateTotals(),
        discount_mode: discountMode,
        discount_value: discountValue
      };
      
      const response = await api.post('/devis-generator/generate-pdf', pdfData, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Devis_${pdfData.devis_info.numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Devis PDF généré !');
      setStep(4);
      onDevisCreated?.();
      
    } catch (error) {
      toast.error('Erreur de génération');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setDescription('');
    setAudioBlob(null);
    setEditedLines([]);
    setProjectTitle('');
    setBeforePhoto(null);
    setBeforePhotoPreview(null);
    setDiscountMode('percent');
    setDiscountValue(0);
    setChantierAdresse('');
    setSameAsClient(true);
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="devis-generator-container">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {[
          { num: 1, label: 'Description' },
          { num: 2, label: 'Génération IA' },
          { num: 3, label: 'Édition' },
          { num: 4, label: 'Terminé' }
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${
              step >= s.num ? 'bg-[#0157b8] text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {step > s.num ? <CheckCircle className="w-4 h-4" /> : s.num}
            </div>
            <span className={`ml-2 text-xs ${step >= s.num ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>
              {s.label}
            </span>
            {idx < 3 && <ArrowRight className="w-3 h-3 text-slate-300 mx-2" />}
          </div>
        ))}
      </div>

      {/* Step 1: Description */}
      {step === 1 && (
        <div className="space-y-5">
          {/* Input mode toggle */}
          <div className="flex justify-center gap-3">
            <Button
              variant={inputMode === 'text' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setInputMode('text')}
              className={inputMode === 'text' ? 'bg-[#0157b8]' : ''}
            >
              <MessageSquare className="w-4 h-4 mr-2" /> Texte
            </Button>
            <Button
              variant={inputMode === 'voice' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setInputMode('voice')}
              className={inputMode === 'voice' ? 'bg-[#0157b8]' : ''}
            >
              <Mic className="w-4 h-4 mr-2" /> Vocal
            </Button>
          </div>

          {/* Project type */}
          <div>
            <Label className="text-sm font-medium">Type de projet</Label>
            <Input
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              placeholder="Ex: Rénovation salle de bain, Isolation combles..."
              className="mt-1"
            />
          </div>

          {/* Text or Voice input */}
          {inputMode === 'text' ? (
            <div>
              <Label className="text-sm font-medium">Description du chantier</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Décrivez les travaux en détail: surfaces, matériaux, équipements..."
                className="mt-1 min-h-[150px]"
              />
            </div>
          ) : (
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              {!isRecording && !audioBlob && (
                <>
                  <Mic className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-600 mb-3">Décrivez votre chantier oralement</p>
                  <Button onClick={startRecording} className="bg-[#0157b8]">
                    <Mic className="w-4 h-4 mr-2" /> Enregistrer
                  </Button>
                </>
              )}
              
              {isRecording && (
                <>
                  <div className="relative inline-block">
                    <Mic className="w-12 h-12 text-red-500 animate-pulse" />
                  </div>
                  <p className="text-red-600 font-medium my-3">Enregistrement...</p>
                  <Button onClick={stopRecording} variant="destructive">
                    <MicOff className="w-4 h-4 mr-2" /> Arrêter
                  </Button>
                </>
              )}
              
              {audioBlob && !isRecording && (
                <>
                  <Volume2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <div className="flex justify-center gap-3">
                    <Button variant="outline" onClick={() => setAudioBlob(null)}>
                      <RefreshCw className="w-4 h-4 mr-2" /> Réenregistrer
                    </Button>
                    <Button onClick={transcribeAudio} disabled={transcribing} className="bg-[#0157b8]">
                      {transcribing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                      Transcrire
                    </Button>
                  </div>
                </>
              )}
              
              {description && (
                <div className="mt-4 p-3 bg-white rounded-lg text-left text-sm">
                  <p className="text-slate-600">{description}</p>
                </div>
              )}
            </div>
          )}

          {/* Client info */}
          <div className="bg-slate-50 rounded-xl p-5">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-[#0157b8]" /> Informations client
            </h4>
            
            {/* Client type */}
            <div className="mb-4">
              <Label className="text-sm font-medium mb-2 block">Type de client</Label>
              <RadioGroup 
                value={clientInfo.type} 
                onValueChange={(v) => setClientInfo(prev => ({ ...prev, type: v }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="particulier" id="particulier" />
                  <Label htmlFor="particulier" className="flex items-center gap-1 cursor-pointer">
                    <User className="w-4 h-4" /> Particulier
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="professionnel" id="professionnel" />
                  <Label htmlFor="professionnel" className="flex items-center gap-1 cursor-pointer">
                    <Building className="w-4 h-4" /> Professionnel
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Nom {clientInfo.type === 'professionnel' ? '/ Société' : ''}</Label>
                <Input
                  value={clientInfo.nom}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, nom: e.target.value }))}
                  placeholder={clientInfo.type === 'professionnel' ? "Nom de l'entreprise" : "Nom du client"}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Téléphone</Label>
                <Input
                  value={clientInfo.telephone}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, telephone: e.target.value }))}
                  placeholder="06 00 00 00 00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Adresse</Label>
                <Input
                  value={clientInfo.adresse}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, adresse: e.target.value }))}
                  placeholder="Adresse complète"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Email</Label>
                <Input
                  value={clientInfo.email}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemple.com"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Chantier address */}
          <div className="bg-slate-50 rounded-xl p-5">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#0157b8]" /> Adresse du chantier
            </h4>
            
            <div className="mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsClient}
                  onChange={(e) => setSameAsClient(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <span className="text-sm">Même adresse que le client</span>
              </label>
            </div>
            
            {!sameAsClient && (
              <Input
                value={chantierAdresse}
                onChange={(e) => setChantierAdresse(e.target.value)}
                placeholder="Adresse du chantier si différente"
              />
            )}
          </div>

          {/* Before photo */}
          <div className="bg-slate-50 rounded-xl p-5">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Camera className="w-4 h-4 text-[#0157b8]" /> Photo avant travaux (optionnel)
            </h4>
            
            <input
              type="file"
              accept="image/*"
              ref={beforePhotoRef}
              onChange={handleBeforePhotoSelect}
              className="hidden"
            />
            
            {beforePhotoPreview ? (
              <div className="relative inline-block">
                <img src={beforePhotoPreview} alt="Avant" className="max-h-48 rounded-lg" />
                <button
                  onClick={() => { setBeforePhoto(null); setBeforePhotoPreview(null); }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => beforePhotoRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 w-full hover:border-[#0157b8] transition-colors flex flex-col items-center gap-2 text-slate-500"
              >
                <ImageIcon className="w-8 h-8" />
                <span className="text-sm">Ajouter une photo du chantier</span>
              </button>
            )}
          </div>

          {/* Generate button */}
          <div className="flex justify-center pt-2">
            <Button 
              onClick={generateLines}
              disabled={!description.trim() || generating}
              size="lg"
              className="bg-gradient-to-r from-[#0157b8] to-[#00428a] hover:from-[#00428a] hover:to-[#003366]"
            >
              {generating ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Sparkles className="w-5 h-5 mr-2" />}
              Générer le devis avec l'IA
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Generating */}
      {step === 2 && (
        <div className="bg-gradient-to-br from-[#0157b8] to-[#00428a] rounded-xl p-12 text-center text-white">
          <Brain className="w-16 h-16 mx-auto mb-4 animate-pulse" />
          <h3 className="text-xl font-bold mb-2">Génération en cours...</h3>
          <p className="text-blue-200">L'IA analyse votre projet</p>
        </div>
      )}

      {/* Step 3: Edit */}
      {step === 3 && (
        <div className="space-y-5">
          {/* Project info */}
          {projectTitle && (
            <div className="bg-[#e8f4fc] rounded-lg p-3 border border-[#0157b8]/20">
              <p className="font-medium text-[#0157b8]">Projet : {projectTitle}</p>
            </div>
          )}

          {/* Discount */}
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              {discountMode === 'percent' ? <Percent className="w-4 h-4 text-green-600" /> : <Euro className="w-4 h-4 text-green-600" />}
              Réduction globale
            </h4>
            
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex gap-2">
                <Button
                  variant={discountMode === 'percent' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setDiscountMode('percent'); setDiscountValue(0); }}
                  className={discountMode === 'percent' ? 'bg-[#0157b8]' : ''}
                >
                  <Percent className="w-3 h-3 mr-1" /> %
                </Button>
                <Button
                  variant={discountMode === 'euro' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setDiscountMode('euro'); setDiscountValue(0); }}
                  className={discountMode === 'euro' ? 'bg-[#0157b8]' : ''}
                >
                  <Euro className="w-3 h-3 mr-1" /> €
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <NumInputFR
                  value={discountValue}
                  onChange={(n) => setDiscountValue(n)}
                  className="w-20 h-9"
                />
                <span className="text-sm text-slate-500">{discountMode === 'percent' ? '%' : '€'}</span>
              </div>
            </div>
          </div>

          {/* Lines */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b flex justify-between items-center">
              <h4 className="font-semibold flex items-center gap-2">
                <Calculator className="w-4 h-4 text-[#0157b8]" />
                Prestations ({editedLines.length})
              </h4>
              <Button size="sm" variant="outline" onClick={addLine}>
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            
            <div className="divide-y">
              {editedLines.map((line, idx) => (
                <div key={line.id} className="p-4 hover:bg-slate-50">
                  <div className="flex gap-3">
                    {/* Image */}
                    <div className="flex-shrink-0">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={el => fileInputRefs.current[idx] = el}
                        onChange={(e) => handleLineImage(idx, e)}
                      />
                      {line.imagePreview ? (
                        <div className="relative">
                          <img src={line.imagePreview} alt="" className="w-20 h-20 object-cover rounded-lg" />
                          <button
                            onClick={() => { updateLine(idx, 'image', null); updateLine(idx, 'imagePreview', null); }}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => fileInputRefs.current[idx]?.click()}
                          className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-[#0157b8] hover:text-[#0157b8]"
                        >
                          <Camera className="w-5 h-5" />
                          <span className="text-xs mt-1">Photo</span>
                        </button>
                      )}
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 space-y-2">
                      <Textarea
                        value={line.description}
                        onChange={(e) => updateLine(idx, 'description', e.target.value)}
                        className="min-h-[60px] text-sm"
                        placeholder="Description"
                      />
                      
                      <div className="grid grid-cols-5 gap-2">
                        <div>
                          <Label className="text-xs">Qté</Label>
                          <NumInputFR
                            value={line.quantite}
                            onChange={(n) => updateLine(idx, 'quantite', (n || 1))}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Unité</Label>
                          <Select value={line.unite} onValueChange={(v) => updateLine(idx, 'unite', v)}>
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['u', 'm²', 'ml', 'm³', 'h', 'j', 'forfait', 'kg', 'L'].map(u => (
                                <SelectItem key={u} value={u}>{u}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">P.U. HT</Label>
                          <NumInputFR
                            value={line.prix_unitaire}
                            onChange={(n) => updateLine(idx, 'prix_unitaire', n)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">TVA</Label>
                          <Select value={line.tva_rate.toString()} onValueChange={(v) => updateLine(idx, 'tva_rate', parseFloat(v))}>
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 5.5, 10, 20].map(t => (
                                <SelectItem key={t} value={t.toString()}>{t}%</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Total HT</Label>
                          <div className="h-8 flex items-center font-semibold text-sm">
                            {calculateLineTotal(line).toFixed(2)} €
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Delete */}
                    <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} className="text-red-500 h-8">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="p-4 bg-slate-50 border-t">
              <div className="flex justify-end">
                <div className="w-64 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Total HT</span>
                    <span className="font-medium">{totals.ht.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>TVA</span>
                    <span className="font-medium">{totals.tva.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-base font-bold border-t pt-2 mt-2">
                    <span>Total TTC</span>
                    <span className="text-green-600">{totals.ttc.toFixed(2)} €</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={resetForm}>
              <RefreshCw className="w-4 h-4 mr-2" /> Recommencer
            </Button>
            <Button 
              onClick={handleGeneratePDF}
              disabled={generatingPdf || editedLines.length === 0}
              className="bg-green-600 hover:bg-green-700"
              size="lg"
            >
              {generatingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Générer le PDF
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Success */}
      {step === 4 && (
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-10 text-center text-white">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">Devis généré !</h3>
          <p className="text-green-100 mb-6">Le PDF a été téléchargé.</p>
          <Button variant="outline" onClick={resetForm} className="bg-white text-green-600 hover:bg-green-50">
            <RefreshCw className="w-4 h-4 mr-2" /> Créer un autre devis
          </Button>
        </div>
      )}
    </div>
  );
}
